<template>
    <VJoyWrapper justify="space-between" :no-margin="true" column-gap="2">
        <VJoyButton
            v-for="displayedLink in ctaDisplayedLinks"
            :key="`admin-links-cta--${displayedLink.id}`"
            :data-testid="`admin-links-link--${displayedLink.id}`"
            :href="displayedLink.href"
            :size="'xsmall'"
            :icon="displayedLink.icon"
            target="blank"
        >
            {{ displayedLink.label }}
        </VJoyButton>

        <VJoyDropdown :options="dropdownDisplayedLinks" justify="right">
            <template #dropdown-button>
                <VJoyIconButton data-testid="admin-links-dropdown-cta" icon="dots-vertical" size="xsmall" variant="ghost" />
            </template>
            <template #dropdown-item="item">
                <VJoyLink
                    :data-testid="`admin-links-dropdown-link--${item.id}`"
                    :href="item.href || ''"
                    :icon="item.icon"
                    :text="item.label"
                    target="_blank"
                />
            </template>
        </VJoyDropdown>
    </VJoyWrapper>
</template>

<script lang="ts" setup>
    import type {ComputedRef} from 'vue';
    import {computed} from 'vue';
    import {useTranslation} from '#imports';
    import {VJoyDropdown, VJoyWrapper, VJoyButton, VJoyIconButton, VJoyLink} from '@maltjoy/core-vue';
    import type {JoyIconsId} from '@maltjoy/icons';
    import type {ProfileAdminLinks} from '@api';

    const props = defineProps<{
        links: ProfileAdminLinks;
    }>();

    const {t} = useTranslation();

    type DisplayedLink = {
        id: string;
        icon: JoyIconsId;
        label: string;
        href: string;
    };

    const dropdownDisplayedLinks: ComputedRef<DisplayedLink[]> = computed(() => {
        const result = [
            {
                id: 'compliance',
                icon: 'legal-check' as JoyIconsId,
                label: t('profile.vetting.admin.links.compliance'),
                href: props.links.compliancePage,
            },
            {
                id: 'dashboard',
                icon: 'freelancer-placeholder' as JoyIconsId,
                label: t('profile.vetting.admin.links.dashboard'),
                href: props.links.dashboardPage,
            },
            {
                id: 'cv-download',
                icon: 'download' as JoyIconsId,
                label: t('profile.vetting.admin.links.cv'),
                href: props.links.downloadCv,
            },
        ];

        if (props.links.salesforcePage) {
            result.push({
                id: 'salesforce_page',
                icon: 'external-link' as JoyIconsId,
                label: t('profile.vetting.admin.links.salesforce_page'),
                href: props.links.salesforcePage,
            });
        }

        if (props.links.comatchPage) {
            result.push({
                id: 'comatch_page',
                icon: 'external-link' as JoyIconsId,
                label: t('profile.vetting.admin.links.comatch_page'),
                href: props.links.comatchPage,
            });
        }

        return result;
    });

    const ctaDisplayedLinks = computed((): DisplayedLink[] => [
        {
            id: 'shake',
            icon: 'external-link',
            label: t('profile.vetting.admin.links.shake'),
            href: props.links.shakePage,
        },
        {
            id: 'resume-edit-and-download',
            icon: 'conditions',
            label: 'Edit PDF Resume',
            href: props.links.editResume,
        },
    ]);
</script>
